import React from 'react';
import { Button } from 'equasion-lib';
import { FiCameraOff, FiX } from 'react-icons/fi';
import ScanModeSwitch, { LaserModePanel } from '@/lib/shared-coms/ScanModeSwitch';
import useZoneScanSession from './useZoneScanSession';
import type { ZoneScanRules } from './useZoneScanSession';
import ZoneScanBanner from './ZoneScanBanner';
import type { ScannedZone } from './zoneByScanCode';
import './ZoneScanner.scss';

// equasion's ButtonProps type omits onClick (it reaches the <button> through ...rest at runtime);
// a spread is not excess-property checked.
const click = (onClick: () => void) => ({ onClick });

interface InlineZoneScannerProps extends ZoneScanRules {
  /** Heading of the zone section while it scans, e.g. "Scan the quarantine zone". */
  title: string;
  onSelect: (zone: ScannedZone) => void;
  /** The X: stop scanning and leave the zone as it was. */
  onClose: () => void;
  /** Switch the zone section to the zone list. */
  onManualEntry: () => void;
  manualEntryLabel: string;
}

/**
 * A live zone scanner built into a form's zone section — a small camera view, or the "pull the
 * trigger" prompt in laser mode — for screens whose other fields (lot, expiration, quantity…)
 * must stay visible while the zone is scanned. Same rules, lookup and messages as the full-screen
 * overlay (useZoneScanSession); only the layout differs.
 *
 * Mounting it claims the scanner and unmounting hands it back, so the zone section renders it only
 * while it is actually waiting for a label.
 */
const InlineZoneScanner = ({
  title,
  onSelect,
  onClose,
  onManualEntry,
  manualEntryLabel,
  ...rules
}: InlineZoneScannerProps) => {
  const {
    targetRef,
    status,
    laserMode,
    cameraMessage,
    hardwareDetected,
    rejectionFor,
    acceptZone,
    scanAgain
  } = useZoneScanSession({ ...rules, onAccepted: onSelect });

  return (
    <div
      className={`zone-scan-inline${laserMode ? ' zone-scan-inline--laser' : ''}`}
      role="group"
      aria-label={title}
    >
      <div className="zone-scan-inline__head">
        <span className="zone-scan-inline__title">{title}</span>
        <ScanModeSwitch className="zone-scan-inline__mode-switch" />
        <button
          type="button"
          className="zone-scan-inline__close"
          onClick={onClose}
          aria-label="Close zone scanner"
        >
          <FiX />
        </button>
      </div>

      <div className="zone-scan-inline__viewport">
        {/* Camera mode only — laser mode never asks for the camera. */}
        {!laserMode && (
          <div className="zone-scan__no-camera">
            <FiCameraOff />
            <span>{cameraMessage(true)}</span>
          </div>
        )}
        {/* Stays mounted in both modes: this element is what claims the scanner. */}
        <div className="zone-scan__camera" ref={targetRef} hidden={laserMode} />
        {laserMode && (
          <LaserModePanel
            detected={hardwareDetected}
            hint="Pull the trigger to scan the zone label"
          />
        )}
      </div>

      <ZoneScanBanner
        inline
        status={status}
        rejectionFor={rejectionFor}
        onChoose={(zone) => acceptZone(zone)}
        onScanAgain={scanAgain}
      />

      <Button type="secondary" fullWidth {...click(onManualEntry)}>
        {manualEntryLabel}
      </Button>
    </div>
  );
};

export default InlineZoneScanner;
